<template>
  <v-app
    class="app"
    :class="
      isLandscape
        ? `pt-8 landscape ${tenantName}`
        : `pt-6 portrait ${tenantName}`
    "
  >
    <BaseHeader
      :title="title"
      :subtitle="subtitle"
      :orientation="orientation"
    />

    <v-main
      class="app__main d-flex justify-center py-0 mt-8"
      :class="
        isLandscape ? 'app__main--landscape px-8' : 'app__main--portrait px-6'
      "
    >
      <slot />
    </v-main>
    <BaseFooter :orientation="orientation" />
  </v-app>
</template>

<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import { reactive } from "vue";
import { useTheme } from "vuetify";
import { Orientation, Page } from "~/types/enum";

const { t } = useI18n();
const route = useRoute();
const theme = useTheme();
const { $dayjs } = useNuxtApp();
$dayjs().locale("de-At");

const tenantName = computed(() => {
  return route.params.tenant ?? "";
});

const state = reactive({
  title: "",
});

watch(
  () => tenantName.value,
  (tenant) => {
    if (tenant === "uko-tower") {
      theme.global.name.value = "ukoTowerTheme";
    } else {
      theme.global.name.value = "defaultTheme";
    }
  },
  { immediate: true }
);

provide("layout", state);

// INFO set header title according to page
const title = computed(() => {
  if (state.title) {
    return state.title;
  }
  const page = route.fullPath;
  if (page === Page.Floorplan) {
    return t("floorplan.title");
  } else if (page.includes(Page.Office)) {
    const roomNumber =
      route.query.number?.toString() ??
      t("validation.query-param-missing", { name: "number" });
    return route.query.number
      ? t("room.office") + " " + roomNumber
      : roomNumber;
  } else if (page.includes(Page.Studio)) {
    const roomNumber =
      route.query.number?.toString() ??
      t("validation.query-param-missing", { name: "number" });
    return route.query.number
      ? t("room.studio") + " " + roomNumber
      : roomNumber;
  } else if (page.includes(Page.MeetingRoom)) {
    const name = (route.query.number as String)?.split(",") ?? [
      t("validation.query-param-missing", { name: "number" }),
    ];
    return name.length > 1
      ? t("meeting-room.title")
      : t("meeting-room.title") + " " + name[0];
  } else if (page === Page.Home) {
    return t("home.title");
  } else {
    return "";
  }
});

const subtitle = computed(() => {
  const page = route.fullPath;
  if (page === Page.Floorplan) {
    return t("floorplan.subtitle");
  }

  if (page.includes(Page.MeetingRoom)) {
    const name = (route.query.number as String)?.split(",") ?? [
      t("validation.query-param-missing", { name: "number" }),
    ];
    return name.length > 1
      ? name[0] + t("meeting-room.to") + name[name.length - 1]
      : "";
  }
});

const orientation = computed(() => {
  const page = route.fullPath;
  const rooms = (route.query.number as String)?.split(",") || [];
  if (
    page.includes(Page.Office) ||
    page.includes(Page.Studio) ||
    (page.includes(Page.MeetingRoom) && rooms.length === 1)
  ) {
    return Orientation.landscape;
  } else {
    return Orientation.portrait;
  }
});

const isLandscape = computed(() => orientation.value === Orientation.landscape);
</script>

<style lang="scss" scoped>
.app {
  position: relative;
  height: 100vh;

  &__main {
    overflow: hidden;

    &--portrait {
      height: $content-height !important;
    }

    &--landscape {
      height: $content-height-landscape !important;
    }
  }

  &.uko-tower {
    &.landscape {
      background: url("/uko-tower/landscape-background.webp") repeat center
        center fixed;
    }
    &.portrait {
      background: url("/uko-tower/portrait-background.webp") repeat center
        center fixed;
    }
  }
}

.footer {
  &__divider {
    position: absolute;
    bottom: 0;
    left: 0;
  }
}
</style>

<style lang="scss">
.app {
  &.uko-tower {
    font-family: "Barlow";
  }
}
</style>
